import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Shield } from "lucide-react";
import { Separator } from "../components/ui/separator";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Helmet>
        <title>Privacy Policy | FutureThought</title>
        <meta name="description" content="FutureThought privacy policy. Learn how we collect, use, and protect your data including cookies, analytics, and advertising practices." />
        <link rel="canonical" href="https://futurethought.emergent.host/privacy-policy" />
      </Helmet>

      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="text-2xl font-bold font-['Playfair_Display']">
              Future<span className="text-[hsl(var(--primary))]">Thought</span>
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Shield size={20} className="text-[hsl(var(--primary))]" />
            <span className="text-sm font-mono tracking-wider uppercase text-[hsl(var(--primary))]">Legal</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-['Playfair_Display'] font-bold tracking-tight mb-8" data-testid="privacy-title">
            Privacy Policy
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mb-12">Last updated: July 2026</p>

          <div className="prose-custom space-y-8 text-[hsl(var(--foreground))] leading-relaxed text-justify">
            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">1. Introduction</h2>
              <p>FutureThought ("we," "our," or "us") operates the website futurethought.emergent.host (the "Site"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our Site. By accessing or using the Site, you agree to this Privacy Policy.</p>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">2. Information We Collect</h2>
              <p className="mb-3"><strong>Information You Provide:</strong> When you subscribe to our newsletter, you provide your email address. We do not collect any other personally identifiable information through our forms.</p>
              <p className="mb-3"><strong>Automatically Collected Information:</strong> When you visit our Site, certain information is collected automatically, including:</p>
              <ul className="list-disc pl-6 space-y-2 text-[hsl(var(--muted-foreground))]">
                <li>Your IP address and approximate geographic location</li>
                <li>Browser type, device type, and operating system</li>
                <li>Pages visited, time spent on pages, and referring URLs</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">3. How We Use Your Information</h2>
              <p>We use collected information to:</p>
              <ul className="list-disc pl-6 space-y-2 text-[hsl(var(--muted-foreground))]">
                <li>Deliver and improve our content and services</li>
                <li>Send newsletter updates to subscribed users</li>
                <li>Analyze website traffic and user behavior to improve the Site</li>
                <li>Display relevant advertisements through Google AdSense</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">4. Cookies and Tracking Technologies</h2>
              <p className="mb-3">Our Site uses cookies and similar technologies to enhance your experience. These include:</p>
              <p className="mb-3"><strong>Essential Cookies:</strong> Required for the Site to function properly, such as session management.</p>
              <p className="mb-3"><strong>Analytics Cookies:</strong> We use Google Analytics (property ID: G-NHRD5XLR2J) to understand how visitors interact with our Site. Google Analytics collects data such as pages visited, session duration, and traffic sources. You can opt out of Google Analytics by installing the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--primary))] hover:underline">Google Analytics Opt-out Browser Add-on</a>.</p>
              <p className="mb-3"><strong>Advertising Cookies:</strong> We use Google AdSense to display advertisements. Google AdSense may use cookies to serve ads based on your prior visits to our Site or other websites. You can opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--primary))] hover:underline">Google Ads Settings</a>.</p>
              <p><strong>Tag Management:</strong> We use Google Tag Manager (GTM-WZRWR7X2) to manage analytics and advertising tags on our Site.</p>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">5. Third-Party Services</h2>
              <p>We use the following third-party services that may collect information about you:</p>
              <ul className="list-disc pl-6 space-y-2 text-[hsl(var(--muted-foreground))]">
                <li><strong>Google AdSense:</strong> Serves advertisements. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--primary))] hover:underline">Google Privacy Policy</a></li>
                <li><strong>Google Analytics:</strong> Provides website analytics. <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[hsl(var(--primary))] hover:underline">Google Privacy Policy</a></li>
                <li><strong>Google Tag Manager:</strong> Manages tracking scripts.</li>
              </ul>
              <p className="mt-3">These third parties have their own privacy policies governing the use of information they collect.</p>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">6. Data Retention</h2>
              <p>Newsletter subscription data (email addresses) is retained for as long as your subscription is active. You may unsubscribe at any time by contacting us. Automatically collected data through analytics is retained according to Google Analytics' default retention settings.</p>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">7. Your Rights</h2>
              <p>Depending on your jurisdiction, you may have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 text-[hsl(var(--muted-foreground))]">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Opt out of personalized advertising</li>
                <li>Unsubscribe from our newsletter</li>
              </ul>
              <p className="mt-3">To exercise any of these rights, please contact us at the email address listed below.</p>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">8. Children's Privacy</h2>
              <p>Our Site is not intended for individuals under the age of 16. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us so we can take appropriate action.</p>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">9. Changes to This Policy</h2>
              <p>We may update this Privacy Policy from time to time. Any changes will be posted on this page with a revised "Last updated" date. We encourage you to review this policy periodically.</p>
            </section>

            <section>
              <h2 className="text-2xl font-['Playfair_Display'] font-semibold mb-4">10. Contact Us</h2>
              <p>If you have questions about this Privacy Policy, please contact us at:</p>
              <p className="mt-2 text-[hsl(var(--primary))]">
                <Link to="/contact" className="hover:underline">Contact Page</Link>
              </p>
            </section>
          </div>
        </div>
      </main>

      <footer className="py-12 px-6 md:px-12 lg:px-24 border-t border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/" className="text-xl font-bold font-['Playfair_Display']">
            Future<span className="text-[hsl(var(--primary))]">Thought</span>
          </Link>
          <div className="flex gap-6 text-sm text-[hsl(var(--muted-foreground))]">
            <span className="text-[hsl(var(--foreground))]">Privacy Policy</span>
            <Link to="/terms" className="hover:text-[hsl(var(--foreground))] transition-colors">Terms of Service</Link>
            <Link to="/contact" className="hover:text-[hsl(var(--foreground))] transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
